/**
 * A camada anti-corrupção da fila de triagem. Mesmo padrão de
 * `features/casos/api/mapeadores.ts`, com uma restrição a mais: os campos
 * aqui SÃO PII (`patientName`/`operator`/`hospital`, e o corpo do e-mail
 * original que `T13` lê à parte) — nenhuma mensagem de erro deste arquivo
 * embute o VALOR de um campo, só o nome do campo e o motivo. É o oposto do
 * padrão de `mapeadores.ts` de casos, que ecoa `item.state` na mensagem
 * porque `state` não é PII.
 *
 * Normalizações de runtime, e só aqui:
 *   1. Data. `parseInstante`/`parseInstanteOuNulo` — mesma disciplina do
 *      erro de três horas que `features/casos` já documenta.
 *   2. Enum. `origin` (`AC-8`), `deadline.source` (`E5`) e `deadline.status`
 *      (`AC-14`/`AC-15`) fora do vocabulário conhecido são ERRO explícito —
 *      nenhum dos três carrega dado de paciente, então o token pode entrar
 *      na mensagem (mesmo espírito de `mapeadores.ts` de casos).
 *   3. `items`/`requestedParameters` fora de forma de array — ERRO.
 *
 * O que este arquivo NUNCA faz: validar o CONTEÚDO de `value`/`humanValue`.
 * Esses campos são texto livre vindo do interpretador — passam direto, sem
 * checagem de forma, porque não há vocabulário fechado para comparar (ao
 * contrário do estado do caso).
 */

import type {
  AuthorizationResult,
  AuthorizationResultSource,
  AuthorizationReturnSnapshot,
  TriagemAttachment,
  TriagemClassificationSnapshot,
  TriagemDetalhe,
  TriagemFieldSnapshot,
  TriagemFilaItemResponse,
  TriagemFilaResponse,
  TriagemMessage,
  TriagemPayerSnapshot,
} from '@anery/api-contracts';

import { ErroDaApi } from '../api/erros';
import { DataInvalida, parseInstante } from '../dominio/datas';
import {
  ehClasseDoTipo,
  ehModalidadeDaClassificacao,
  ehMotivoDeConferencia,
  ehOrigemDaClassificacao,
  ehOrigemDoCampo,
  ehOrigemDoPrazo,
  ehSituacaoDoPrazo,
  ehTipoDeMensagem,
  type AnexoDaTriagem,
  type CampoClinico,
  type CampoDaTriagem,
  type ClassificacaoDaTriagem,
  type FilaDeTriagem,
  type MensagemDaTriagem,
  type PrazoDaFila,
  type RetornoDaMensagem,
  type TicketDaTriagem,
  type TriagemClinicalFieldSnapshot,
  type TriagemDetalhada,
} from './tipos';

/**
 * Resposta que não casa com o contrato da triagem.
 *
 * `origem: 'formato'` para a tela desenhar o mesmo `EstadoDeErro` de
 * sempre, sem "tentar de novo": o corpo inválido volta idêntico.
 */
export class ErroDeContratoDeTriagem extends ErroDaApi {
  constructor(
    readonly campo: string,
    motivo: string,
  ) {
    super({
      mensagem: `A API devolveu uma triagem fora do contrato (${campo}): ${motivo}.`,
      origem: 'formato',
      codigo: 'TRIAGEM_CONTRACT_MISMATCH',
    });
    this.name = 'ErroDeContratoDeTriagem';
  }
}

function texto(valor: string, campo: string): string {
  if (valor.trim() === '') throw new ErroDeContratoDeTriagem(campo, 'veio vazio');
  return valor;
}

function instanteObrigatorio(valor: string, campo: string) {
  try {
    return parseInstante(valor);
  } catch (causa) {
    if (causa instanceof DataInvalida) throw new ErroDeContratoDeTriagem(campo, causa.message);
    throw causa;
  }
}

function instanteOpcional(valor: string | null | undefined, campo: string) {
  if (valor === null || valor === undefined) return null;
  return instanteObrigatorio(valor, campo);
}

/**
 * `TriagemFieldSnapshot` (fio) → `CampoDaTriagem` (domínio).
 *
 * `nomeDoCampo` só identifica ONDE, nunca O QUE — a mensagem de erro nunca
 * ecoa `snapshot.value`/`snapshot.humanValue`, que são PII.
 */
export function mapearCampo(snapshot: TriagemFieldSnapshot, nomeDoCampo: string): CampoDaTriagem {
  let origem: CampoDaTriagem['origem'] = null;
  // `ExtractionSource?` do C# serializa como `null` explícito quando o campo
  // nunca foi tentado, mas o tipo gerado só admite ausente (enum nullable é
  // ponto cego do Swashbuckle). Lido como `unknown` para a checagem de `null`
  // não ser "impossível" para o compilador — é a API real que manda.
  const origemBruta: unknown = snapshot.origin;
  if (origemBruta !== undefined && origemBruta !== null) {
    if (!ehOrigemDoCampo(origemBruta)) {
      // O TOKEN de origem (`Rule`/`Agent`/`Human`) não é PII — é vocabulário
      // fechado, o mesmo espírito de ecoar `item.state` no mapeador de casos.
      throw new ErroDeContratoDeTriagem(
        `${nomeDoCampo}.origin`,
        `${JSON.stringify(origemBruta)} não é uma origem conhecida`,
      );
    }
    origem = origemBruta;
  }

  return {
    tentado: snapshot.attempted,
    encontrado: snapshot.found,
    valor: snapshot.value ?? null,
    valorHumano: snapshot.humanValue ?? null,
    confianca: snapshot.confidence ?? null,
    limiar: snapshot.threshold ?? null,
    origem,
    // `TP-030`: `null` no item de lista (o mapper da API não preenche `id`
    // ali, `FC-4`) e no DETALHE quando o campo nunca foi tentado — só a
    // linha real de `campo_extraido` tem um id para confirmar.
    id: snapshot.id ?? null,
  };
}

/** `TriagemClinicalFieldSnapshot` (fio) → `CampoClinico` (domínio). SEM valor. */
export function mapearCampoClinico(snapshot: TriagemClinicalFieldSnapshot): CampoClinico {
  return { tentado: snapshot.attempted, encontrado: snapshot.found };
}

/**
 * `TriagemAttachment` (fio) → `AnexoDaTriagem` (domínio, `T16`).
 *
 * `nomeArquivo` pode conter dado do paciente (nome do arquivo escrito por
 * quem anexou) — mesma disciplina do resto deste arquivo: `texto()` nunca
 * ecoa o VALOR, só o nome do campo e o motivo.
 */
export function mapearAnexo(anexo: TriagemAttachment, indice: number, caminho = 'attachments'): AnexoDaTriagem {
  return {
    id: texto(anexo.id, `${caminho}[${String(indice)}].id`),
    // `fileName` é `string | null` no contrato gerado (mesma folga de
    // `shortCode`) mas semanticamente obrigatório — `null`/vazio vira o
    // mesmo erro de contrato, nunca um nome de arquivo em branco na tela.
    nomeArquivo: texto(anexo.fileName ?? '', `${caminho}[${String(indice)}].fileName`),
    tamanhoBytes: anexo.sizeBytes ?? null,
  };
}

/** `TriagemMessage` (fio) → `MensagemDaTriagem` (domínio, `US-035` `AC9`). */
export function mapearMensagem(mensagem: TriagemMessage, indice: number): MensagemDaTriagem {
  const campo = `messages[${String(indice)}]`;
  return {
    id: texto(mensagem.id, `${campo}.id`),
    recebidaEm: instanteObrigatorio(mensagem.receivedAt, `${campo}.receivedAt`),
    // Assunto vazio e ausente são a mesma coisa para a tela: "(sem assunto)".
    assunto: mensagem.subject == null || mensagem.subject.trim() === '' ? null : mensagem.subject,
    ehOrigem: mensagem.isOrigin,
    anexos: (mensagem.attachments ?? []).map((anexo, i) => mapearAnexo(anexo, i, `${campo}.attachments`)),
    retorno: mapearRetornoDaMensagem(mensagem.return, `${campo}.return`),
  };
}

const RESULTADOS_DO_RETORNO: readonly AuthorizationResult[] = [
  'Authorized',
  'FirstDayReleased',
  'Extended',
  'PendingDocuments',
  'Denied',
];
const ORIGENS_DO_RESULTADO: readonly AuthorizationResultSource[] = ['Agent', 'Human'];
const DECISOES_DO_RETORNO: readonly NonNullable<RetornoDaMensagem['decisao']>[] = ['Confirmed', 'Discarded'];

function umDe<T extends string>(lista: readonly T[], bruto: unknown): bruto is T {
  return typeof bruto === 'string' && (lista as readonly string[]).includes(bruto);
}

/** `AuthorizationReturnSnapshot` (fio, `TP-056`) → `RetornoDaMensagem`. Ausente e `null` são "sem resultado". */
export function mapearRetornoDaMensagem(
  retorno: AuthorizationReturnSnapshot | null | undefined,
  campo: string,
): RetornoDaMensagem | null {
  if (retorno === null || retorno === undefined) return null;
  if (!umDe(RESULTADOS_DO_RETORNO, retorno.result)) {
    throw new ErroDeContratoDeTriagem(
      `${campo}.result`,
      `${JSON.stringify(retorno.result)} não é um resultado conhecido`,
    );
  }
  if (!umDe(ORIGENS_DO_RESULTADO, retorno.source)) {
    throw new ErroDeContratoDeTriagem(
      `${campo}.source`,
      `${JSON.stringify(retorno.source)} não é uma origem conhecida`,
    );
  }
  // `ReturnDecision?` do C# chega ausente ou `null`: enum nullable é ponto cego do gerador.
  const decisaoBruta: unknown = retorno.decision;
  if (decisaoBruta !== undefined && decisaoBruta !== null && !umDe(DECISOES_DO_RETORNO, decisaoBruta)) {
    throw new ErroDeContratoDeTriagem(
      `${campo}.decision`,
      `${JSON.stringify(decisaoBruta)} não é uma decisão conhecida`,
    );
  }
  return {
    resultado: retorno.result,
    origem: retorno.source,
    confianca: typeof retorno.confidence === 'number' ? retorno.confidence : null,
    decisao: umDe(DECISOES_DO_RETORNO, decisaoBruta) ? decisaoBruta : null,
  };
}

function mapearPrazoDaFila(item: TriagemFilaItemResponse): PrazoDaFila {
  const { deadline } = item;
  if (!ehOrigemDoPrazo(deadline.source)) {
    throw new ErroDeContratoDeTriagem(
      'deadline.source',
      `${JSON.stringify(deadline.source)} não é uma origem de prazo conhecida`,
    );
  }
  if (!ehSituacaoDoPrazo(deadline.status)) {
    throw new ErroDeContratoDeTriagem(
      'deadline.status',
      `${JSON.stringify(deadline.status)} não é uma situação de prazo conhecida`,
    );
  }

  return {
    valor: instanteOpcional(deadline.value ?? null, 'deadline.value'),
    origem: deadline.source,
    valorDoTicket: instanteOpcional(deadline.ticketValue ?? null, 'deadline.ticketValue'),
    situacao: deadline.status,
  };
}

/**
 * `messageKind` (`TP-028`) → `TipoDeMensagem | null`. `undefined`/ausente é
 * estado legítimo (e-mail ainda não classificado, ou parser anterior à
 * T028) — vira `null`, não erro. Valor presente mas fora do vocabulário
 * conhecido é erro de contrato, mesmo espírito de `origin`/`deadline.source`.
 */
function mapearTipoDeMensagem(bruto: unknown, campo: string): TicketDaTriagem['tipoDeMensagem'] {
  if (bruto === undefined || bruto === null) return null;
  if (!ehTipoDeMensagem(bruto)) {
    throw new ErroDeContratoDeTriagem(campo, `${JSON.stringify(bruto)} não é uma classe de mensagem conhecida`);
  }
  return bruto;
}

/**
 * `TriagemClassificationSnapshot` (fio) → `ClassificacaoDaTriagem` (domínio). Ausente ou `null` = ainda não lido.
 *
 * Classe, origem e motivo são vocabulário fechado, não PII: fora da lista é ERRO explícito, e o token pode entrar
 * na mensagem. O texto lido de operadora e de tipo nunca passa por aqui.
 */
export function mapearClassificacao(
  classificacao: TriagemClassificationSnapshot | null | undefined,
): ClassificacaoDaTriagem | null {
  if (classificacao === null || classificacao === undefined) return null;

  // `RequestTypeClass?` do C# serializa como ausente ou `null`: enum nullable é ponto cego do gerador.
  const classeBruta: unknown = classificacao.requestTypeClass;
  let classeDoTipo: ClassificacaoDaTriagem['classeDoTipo'] = null;
  if (classeBruta !== undefined && classeBruta !== null) {
    if (!ehClasseDoTipo(classeBruta)) {
      throw new ErroDeContratoDeTriagem(
        'classification.requestTypeClass',
        `${JSON.stringify(classeBruta)} não é uma classe conhecida`,
      );
    }
    classeDoTipo = classeBruta;
  }

  // `TP-039`: a modalidade vem pronta do servidor. `Modality?` do C# também chega ausente ou `null`.
  const modalidadeBruta: unknown = classificacao.modality;
  let modalidade: ClassificacaoDaTriagem['modalidade'] = null;
  if (modalidadeBruta !== undefined && modalidadeBruta !== null) {
    if (!ehModalidadeDaClassificacao(modalidadeBruta)) {
      throw new ErroDeContratoDeTriagem(
        'classification.modality',
        `${JSON.stringify(modalidadeBruta)} não é AD nem ID`,
      );
    }
    modalidade = modalidadeBruta;
  }

  const ventilacaoBruta: unknown = classificacao.continuousVentilation;
  if (typeof ventilacaoBruta !== 'boolean') {
    throw new ErroDeContratoDeTriagem('classification.continuousVentilation', 'esperado um booleano');
  }
  // Ventilação contínua é atributo de `ID`: com outra modalidade (ou sem nenhuma) a resposta se contradiz.
  if (ventilacaoBruta && modalidade !== 'ID') {
    throw new ErroDeContratoDeTriagem('classification.continuousVentilation', 'ventilação contínua sem modalidade ID');
  }

  const origemBruta: unknown = classificacao.source;
  if (!ehOrigemDaClassificacao(origemBruta)) {
    throw new ErroDeContratoDeTriagem(
      'classification.source',
      `${JSON.stringify(origemBruta)} não é uma origem conhecida`,
    );
  }

  const motivosBrutos: unknown = classificacao.reviewReasons ?? [];
  if (!Array.isArray(motivosBrutos)) {
    throw new ErroDeContratoDeTriagem('classification.reviewReasons', 'esperado um array de motivos');
  }
  const motivos = motivosBrutos.map((motivo: unknown) => {
    if (!ehMotivoDeConferencia(motivo)) {
      throw new ErroDeContratoDeTriagem(
        'classification.reviewReasons',
        `${JSON.stringify(motivo)} não é um motivo conhecido`,
      );
    }
    return motivo;
  });

  // `payer` ausente OU `null` explícito: objeto nullable do .NET que o gerador tipa só como ausente.
  const operadora = classificacao.payer as TriagemPayerSnapshot | null | undefined;
  return {
    classeDoTipo,
    modalidade,
    ventilacaoContinua: ventilacaoBruta,
    operadora:
      operadora === null || operadora === undefined
        ? null
        : {
            id: texto(operadora.id, 'classification.payer.id'),
            codigo: operadora.code ?? '',
            nome: operadora.name ?? '',
          },
    origem: origemBruta,
    aConferir: classificacao.needsReview,
    motivos,
  };
}

/** `TriagemFilaItemResponse` (fio) → `TicketDaTriagem` (domínio). */
export function mapearTicketDaTriagem(item: TriagemFilaItemResponse): TicketDaTriagem {
  return {
    id: texto(item.id, 'id'),
    codigoCurto: item.shortCode ?? null,
    nucleo: item.nucleus ?? null,
    nomeDoNucleo: item.nucleusName ?? null,
    chegadaEm: instanteOpcional(item.arrivedAt ?? null, 'arrivedAt'),
    contagemDeMensagens: item.messageCount,
    temEmailDeOrigem: item.hasOriginEmail,
    assunto: item.latestSubject ?? null,
    paciente: mapearCampo(item.patientName, 'patientName'),
    operadora: mapearCampo(item.operator, 'operator'),
    hospital: mapearCampo(item.hospital, 'hospital'),
    tipoDeSolicitacao: mapearCampo(item.requestType, 'requestType'),
    prazo: mapearPrazoDaFila(item),
    precisaConfirmacao: item.needsConfirmation,
    tipoDeMensagem: mapearTipoDeMensagem(item.messageKind, 'messageKind'),
    numeroSiai: item.siaiNumber ?? null,
    ultimaMensagemEm: instanteOpcional(item.latestMessageAt ?? null, 'latestMessageAt'),
    classificacao: mapearClassificacao(item.classification),
  };
}

/** `GET /triagem` devolve envelope com `total` — diferente do array puro de `/casos`. */
export function mapearFilaDeTriagem(resposta: TriagemFilaResponse): FilaDeTriagem {
  if (!Array.isArray(resposta.items)) {
    throw new ErroDeContratoDeTriagem('items', 'esperado um array de tickets');
  }
  return {
    itens: resposta.items.map(mapearTicketDaTriagem),
    pagina: resposta.page,
    tamanhoDaPagina: resposta.pageSize,
    total: resposta.total,
  };
}

/** `TriagemDetalhe` (fio) → `TriagemDetalhada` (domínio). Os dezenove campos de `AC-28`/`TP-026`/`TP-027`/`TP-029`. */
export function mapearTriagemDetalhada(detalhe: TriagemDetalhe): TriagemDetalhada {
  const parametros = detalhe.requestedParameters ?? [];
  if (!Array.isArray(parametros)) {
    throw new ErroDeContratoDeTriagem('requestedParameters', 'esperado um array de campos');
  }

  const anexos = detalhe.attachments ?? [];
  if (!Array.isArray(anexos)) {
    throw new ErroDeContratoDeTriagem('attachments', 'esperado um array de anexos');
  }

  const mensagens = detalhe.messages ?? [];
  if (!Array.isArray(mensagens)) {
    throw new ErroDeContratoDeTriagem('messages', 'esperado um array de mensagens');
  }

  return {
    id: texto(detalhe.id, 'id'),
    codigoCurto: detalhe.shortCode ?? null,
    nucleo: detalhe.nucleus ?? null,
    temEmailDeOrigem: detalhe.hasOriginEmail,
    paciente: mapearCampo(detalhe.patientName, 'patientName'),
    cpf: mapearCampo(detalhe.taxId, 'taxId'),
    matricula: mapearCampo(detalhe.memberNumber, 'memberNumber'),
    cid: mapearCampoClinico(detalhe.icd),
    operadora: mapearCampo(detalhe.operator, 'operator'),
    hospital: mapearCampo(detalhe.hospital, 'hospital'),
    tipoDeSolicitacao: mapearCampo(detalhe.requestType, 'requestType'),
    parametrosSolicitados: parametros.map((parametro, indice) =>
      mapearCampo(parametro, `requestedParameters[${String(indice)}]`),
    ),
    prazo: mapearCampo(detalhe.deadline, 'deadline'),
    protocolo: mapearCampo(detalhe.protocol, 'protocol'),
    dataNascimento: mapearCampo(detalhe.dateOfBirth, 'dateOfBirth'),
    responsavel: mapearCampo(detalhe.guardian, 'guardian'),
    telefone: mapearCampo(detalhe.phone, 'phone'),
    endereco: mapearCampo(detalhe.address, 'address'),
    dispositivo: mapearCampo(detalhe.device, 'device'),
    solicitante: mapearCampo(detalhe.requester, 'requester'),
    liminar: mapearCampo(detalhe.injunctionFlag, 'injunctionFlag'),
    hd: mapearCampoClinico(detalhe.diagnosticHypothesis),
    frequencia: mapearCampo(detalhe.frequency, 'frequency'),
    anexos: anexos.map((anexo, i) => mapearAnexo(anexo, i)),
    numeroSiai: detalhe.siaiNumber ?? null,
    ultimaMensagemEm: instanteOpcional(detalhe.latestMessageAt ?? null, 'latestMessageAt'),
    contagemDeMensagens: detalhe.messageCount,
    mensagens: mensagens.map(mapearMensagem),
    classificacao: mapearClassificacao(detalhe.classification),
    operadorasParaEscolha: (detalhe.payerOptions ?? []).map((operadora, indice) => ({
      id: texto(operadora.id, `payerOptions[${String(indice)}].id`),
      codigo: operadora.code ?? '',
      nome: operadora.name ?? '',
    })),
  };
}
